import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Modal, Select, message } from 'antd';
import { ArrowLeft, Check, Pencil, Printer, Send, Truck, X } from 'lucide-react';
import Layout from '../../Layout';
import { EXPERTS } from './khoDemo';
import { khoApi, useKho } from './khoStore';
import {
  DOC_TYPE, docById, docValue, docWarnings, fmtMoney, fmtNum, fmtQty, hasControlled, inTransit, itemById, normVariance, serviceById, supplierById,
  validateDoc, whById, type StockDoc,
} from './khoLogic';
import { DocStatusTag, DocTypeTag, ExpiryText, ItemCell, PageHeader, Panel, fmtDateTime, useKhoPerms } from './khoUi';

export const editUrl = (d: StockDoc) =>
  d.type === 'import' ? `/import-export/add-import?id=${d.id}` : d.type === 'count' ? `/import-export/create-inventory-count?id=${d.id}` : `/import-export/add-export?id=${d.id}`;

/** Chi tiết phiếu kho + duyệt / từ chối / nhận hàng chuyển kho */
export default function DocDetailPage() {
  const s = useKho();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canCreate, canApprove, me } = useKhoPerms();
  const d = docById(s, params.get('id'));
  const [modal, setModal] = useState<null | 'approve' | 'reject' | 'receive'>(null);
  const [expert, setExpert] = useState<string>();
  const [text, setText] = useState('');
  const now = Date.now();

  if (!d) {
    return <Layout><Alert type="warning" showIcon message="Không tìm thấy phiếu" action={<Link to="/import-export">Về danh sách phiếu</Link>} /></Layout>;
  }

  const errors = d.status === 'pending' || d.status === 'draft' ? validateDoc(s, d, now) : [];
  const warnings = d.status === 'pending' || d.status === 'draft' ? docWarnings(s, d, now) : [];
  const controlled = hasControlled(s, d);
  const run = (r: { ok: boolean; errors?: string[] }, ok: string) => {
    if (r.ok) { message.success(ok); setModal(null); setText(''); }
    else Modal.error({ title: 'Chưa thực hiện được', content: <ul className="list-disc pl-4">{(r.errors || []).map(e => <li key={e}>{e}</li>)}</ul> });
  };

  const actions = (
    <>
      <Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>Quay lại</Button>
      <Button icon={<Printer size={14} />} onClick={() => window.print()}>In</Button>
      {canCreate && (d.status === 'draft' || d.status === 'rejected') && <Button icon={<Pencil size={14} />} onClick={() => navigate(editUrl(d))}>Sửa</Button>}
      {canCreate && d.status === 'draft' && <Button type="primary" icon={<Send size={14} />} onClick={() => run(khoApi.submitDoc(d.id, me), 'Đã gửi duyệt')}>Gửi duyệt</Button>}
      {canCreate && ['draft', 'pending', 'rejected'].includes(d.status) && (
        <Button danger onClick={() => Modal.confirm({ title: `Hủy phiếu ${d.code}?`, content: 'Phiếu hủy không còn hiệu lực và không ảnh hưởng tồn kho.', okText: 'Hủy phiếu', okButtonProps: { danger: true }, cancelText: 'Đóng', onOk: () => run(khoApi.cancelDoc(d.id, me), 'Đã hủy phiếu') })}>Hủy phiếu</Button>
      )}
      {canApprove && d.status === 'pending' && <>
        <Button danger icon={<X size={14} />} onClick={() => setModal('reject')}>Từ chối</Button>
        <Button type="primary" icon={<Check size={14} />} disabled={errors.length > 0} onClick={() => (controlled ? setModal('approve') : run(khoApi.approveDoc(d.id, me), 'Đã duyệt — tồn kho đã cập nhật'))}>Duyệt</Button>
      </>}
      {canCreate && inTransit(d) && <Button type="primary" icon={<Truck size={14} />} onClick={() => setModal('receive')}>Xác nhận đã nhận</Button>}
    </>
  );

  const info: [string, React.ReactNode][] = [
    ['Loại phiếu', <DocTypeTag type={d.type} />],
    ['Ngày chứng từ', fmtDateTime(d.date)],
    [d.type === 'import' ? 'Nhập vào kho' : d.type === 'count' ? 'Kho kiểm' : 'Xuất từ kho', whById(s, d.warehouseId)?.name],
    ...(d.type === 'transfer' ? [['Kho nhận', whById(s, d.toWarehouseId)?.name] as [string, React.ReactNode]] : []),
    ...(d.type === 'import' ? [['Nhà cung cấp', supplierById(s, d.supplierId)?.name || '—'], ['Số hóa đơn / đơn mua', d.refCode || '—']] as [string, React.ReactNode][] : []),
    ...(d.serviceId ? [['Dịch vụ', serviceById(s, d.serviceId)?.name], ['Người thực hiện', d.practitioner || '—']] as [string, React.ReactNode][] : []),
    ...(d.customer ? [['Khách hàng', d.customer.phone
      ? <Link to={`/customer-demo/detail?phone=${encodeURIComponent(d.customer.phone)}`} className="text-[#3e91ab]">{d.customer.name} · {d.customer.phone}</Link>
      : d.customer.name]] as [string, React.ReactNode][] : []),
    ...(d.reason ? [['Lý do', d.reason]] as [string, React.ReactNode][] : []),
    ['Người lập', `${d.createdBy} · ${fmtDateTime(d.createdAt)}`],
    ...(d.approvedBy ? [['Người duyệt', `${d.approvedBy} · ${fmtDateTime(d.approvedAt!)}`]] as [string, React.ReactNode][] : []),
    ...(d.expertBy ? [['Duyệt chuyên môn', d.expertBy]] as [string, React.ReactNode][] : []),
    ...(d.receivedBy ? [['Người nhận hàng', `${d.receivedBy} · ${fmtDateTime(d.receivedAt!)}`]] as [string, React.ReactNode][] : []),
    ...(d.note ? [['Ghi chú', d.note]] as [string, React.ReactNode][] : []),
  ];

  const variance = d.type === 'issue_service' ? normVariance(s, d) : [];
  const isCount = d.type === 'count';
  const isImport = d.type === 'import';

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title={`${DOC_TYPE[d.type].label} ${d.code}`} desc={`${whById(s, d.warehouseId)?.name || ''} · ${fmtMoney(docValue(d))}`} extra={actions} />
        <div className="flex flex-wrap items-center gap-2 -mt-2"><DocStatusTag doc={d} />{controlled && <span className="text-xs text-violet-700">Có thuốc kiểm soát</span>}</div>

        {d.status === 'rejected' && <Alert type="error" showIcon message={`Bị từ chối: ${d.rejectReason || ''}`} description={canCreate ? 'Bấm "Sửa" để chỉnh và gửi duyệt lại.' : undefined} />}
        {inTransit(d) && <Alert type="info" showIcon message="Hàng đang trên đường chuyển" description={`Đã trừ khỏi ${whById(s, d.warehouseId)?.name}. Hàng chỉ vào ${whById(s, d.toWarehouseId)?.name} khi kho nhận xác nhận.`} />}
        {errors.length > 0 && <Alert type="error" showIcon message={d.status === 'pending' ? 'Chưa duyệt được phiếu này' : 'Cần sửa trước khi gửi duyệt'} description={<ul className="list-disc pl-4 m-0">{errors.map(e => <li key={e}>{e}</li>)}</ul>} />}
        {warnings.length > 0 && <Alert type="warning" showIcon message="Lưu ý khi duyệt" description={<ul className="list-disc pl-4 m-0">{warnings.map(e => <li key={e}>{e}</li>)}</ul>} />}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start">
          <div className="flex flex-col gap-4 min-w-0">
            <Panel title="Thông tin phiếu">
              <dl className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                {info.map(([k, v]) => (
                  <div key={k}><dt className="text-xs text-gray-400">{k}</dt><dd className="text-gray-800 dark:text-gray-200 m-0">{v}</dd></div>
                ))}
              </dl>
            </Panel>

            <Panel title={`Hàng hóa (${d.lines.length} dòng)`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Mặt hàng</th>
                      <th className="text-left font-medium px-3 py-2">Số lô</th>
                      <th className="text-left font-medium px-3 py-2">Hạn dùng</th>
                      {isImport && <th className="text-right font-medium px-3 py-2">SL chứng từ</th>}
                      {isCount && <th className="text-right font-medium px-3 py-2">Sổ sách</th>}
                      <th className="text-right font-medium px-3 py-2">{isCount ? 'Thực tế' : isImport ? 'Thực nhận' : 'Số lượng'}</th>
                      {isCount && <th className="text-right font-medium px-3 py-2">Chênh lệch</th>}
                      <th className="text-right font-medium px-3 py-2">{isImport ? 'Đơn giá' : 'Giá vốn'}</th>
                      <th className="text-right font-medium px-3 py-2">{isCount ? 'Giá trị lệch' : 'Thành tiền'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lines.map(l => {
                      const item = itemById(s, l.itemId);
                      const diff = (l.counted ?? 0) - (l.systemQty ?? 0);
                      return (
                        <tr key={l.id} className={`border-t border-gray-100 dark:border-gray-800 ${isCount && diff ? 'bg-amber-50/50' : ''}`}>
                          <td className="px-3 py-2"><ItemCell item={item} /></td>
                          <td className="px-3 py-2 font-mono text-xs">{l.lotNo}</td>
                          <td className="px-3 py-2"><ExpiryText expiry={l.expiry} now={now} /></td>
                          {isImport && <td className={`px-3 py-2 text-right ${l.docQty !== undefined && l.docQty !== l.qty ? 'text-rose-600 font-medium' : ''}`}>{l.docQty !== undefined ? fmtNum(l.docQty) : '—'}</td>}
                          {isCount && <td className="px-3 py-2 text-right">{fmtNum(l.systemQty ?? 0)}</td>}
                          <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{isCount ? fmtNum(l.counted ?? 0) : fmtQty(item, l.qty)}</td>
                          {isCount && <td className={`px-3 py-2 text-right font-semibold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-rose-600' : 'text-gray-300'}`}>{diff > 0 ? `+${fmtNum(diff)}` : fmtNum(diff)}</td>}
                          <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMoney(l.unitCost)}<span className="text-[11px] text-gray-400">/{item?.unit}</span></td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMoney(isCount ? diff * l.unitCost : l.qty * l.unitCost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200 dark:border-gray-700">
                      <td colSpan={isCount ? 7 : isImport ? 6 : 5} className="px-3 py-2 text-right text-gray-500">Tổng</td>
                      <td className="px-3 py-2 text-right font-bold">{fmtMoney(docValue(d))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Panel>

            {variance.length > 0 && (
              <Panel title={`So với định mức — ${serviceById(s, d.serviceId)?.name}`}>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {variance.map(v => {
                    const item = itemById(s, v.itemId);
                    const pct = v.norm ? Math.round(((v.actual - v.norm) / v.norm) * 100) : null;
                    return (
                      <div key={v.itemId} className="flex justify-between gap-2 border border-gray-100 dark:border-gray-800 rounded-lg px-3 py-2">
                        <span className="truncate">{item?.name}</span>
                        <span className={`whitespace-nowrap ${pct === null ? 'text-amber-600' : pct > 20 ? 'text-rose-600 font-semibold' : pct > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {fmtNum(v.actual)}/{fmtNum(v.norm)} {item?.unit}{pct === null ? ' · ngoài định mức' : pct ? ` · ${pct > 0 ? '+' : ''}${pct}%` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            )}
          </div>

          <Panel title="Lịch sử phiếu">
            <ol className="p-4 flex flex-col gap-3 m-0 list-none">
              {[...d.log].reverse().map((x, i) => (
                <li key={i} className="flex gap-3">
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${i === 0 ? 'bg-[#3e91ab]' : 'bg-gray-300'}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800 dark:text-gray-200">{x.text}</div>
                    <div className="text-[11px] text-gray-400">{x.by} · {fmtDateTime(x.at)}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        </div>
      </div>

      <Modal open={modal === 'approve'} title="Duyệt phiếu có thuốc kiểm soát" okText="Duyệt" cancelText="Đóng" okButtonProps={{ disabled: !expert }}
        onCancel={() => setModal(null)} onOk={() => run(khoApi.approveDoc(d.id, me, expert), 'Đã duyệt — tồn kho đã cập nhật')}>
        <p className="text-sm text-gray-600">Phiếu có thuốc kiểm soát. Chọn bác sĩ / điều dưỡng trưởng chịu trách nhiệm chuyên môn:</p>
        <Select className="w-full" placeholder="Người duyệt chuyên môn" value={expert} onChange={setExpert} options={EXPERTS.map(e => ({ value: e, label: e }))} />
      </Modal>
      <Modal open={modal === 'reject'} title={`Từ chối phiếu ${d.code}`} okText="Từ chối" okButtonProps={{ danger: true, disabled: !text.trim() }} cancelText="Đóng"
        onCancel={() => setModal(null)} onOk={() => run(khoApi.rejectDoc(d.id, me, text), 'Đã từ chối phiếu')}>
        <Input.TextArea autoFocus rows={3} placeholder="Lý do từ chối (bắt buộc) — người lập sẽ thấy để sửa lại" value={text} onChange={e => setText(e.target.value)} />
      </Modal>
      <Modal open={modal === 'receive'} title="Xác nhận đã nhận hàng" okText="Đã nhận đủ" cancelText="Đóng"
        onCancel={() => setModal(null)} onOk={() => run(khoApi.receiveTransfer(d.id, me, text.trim() || undefined), `Đã nhận hàng vào ${whById(s, d.toWarehouseId)?.name}`)}>
        <p className="text-sm text-gray-600">Kiểm đủ số lượng, số lô, hạn dùng và nhiệt độ bảo quản (hàng lạnh) trước khi xác nhận.</p>
        <Input.TextArea rows={2} placeholder="Ghi chú khi nhận (không bắt buộc)" value={text} onChange={e => setText(e.target.value)} />
      </Modal>
    </Layout>
  );
}
